import { Link } from 'react-router-dom';
import { ShopOnboardingID, ShopOnboardingSteps } from '@prisma/client';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckCircle from '@mui/icons-material/CheckCircleRounded';
import RadioUnchecked from '@mui/icons-material/RadioButtonUncheckedOutlined';
import {
  OnboardingModalCategory,
  OnboardingModalStep,
} from '../../../types/businesses';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import { ONBOARDING_STEPS_ROUTES } from '../../../constants/businesses';
import useSkipOnboardingStep from '../../../hooks/businesses/useSkipOnboardingStep';

interface Props {
  category: OnboardingModalCategory;
  setCategory: (category: OnboardingModalCategory) => void;
  step: OnboardingModalStep;
}

function StepRow({ category, setCategory, step }: Props) {
  const theme = useTheme();
  const { onboardings, categoryCompleted, refetch, getCategoryProgress } =
    useGetOnboarding();
  const {
    mutate: skipStep,
    isPending: isSkipping,
    variables: stepBeingSkipped,
  } = useSkipOnboardingStep({
    onSuccess: () => {
      refetch();
    },
  });

  const onboarding =
    category === 'SETUP'
      ? null
      : onboardings?.find(onb => onb.stepId === step.id);
  const isComplete =
    category === 'SETUP'
      ? categoryCompleted[step.category]
      : ['COMPLETE', 'SKIPPED'].includes(onboarding?.status || '');
  const route =
    category === 'SETUP'
      ? null
      : ONBOARDING_STEPS_ROUTES[(step as ShopOnboardingSteps).id];

  const { completed: categoryProgress, total: categoryTotal } =
    getCategoryProgress(step.category);

  const isRoute = !isComplete && !!route;
  const isClickable = isRoute || category === 'SETUP';
  const isStepBeingSkipped = stepBeingSkipped === step.id && isSkipping;
  const Icon = isComplete ? CheckCircle : RadioUnchecked;

  const onClick = () => {
    if (category === 'SETUP') {
      setCategory(step.category);
    } else {
      window.scrollTo(0, 0);
    }
  };

  const onSkip = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const isValidId = Object.values(ShopOnboardingID).includes(
      step.id as ShopOnboardingID,
    );
    if (!isValidId || !step.isSkippable) return;
    skipStep(step.id as ShopOnboardingID);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      px={0.25}
      sx={{
        ':hover': {
          backgroundColor: isClickable
            ? theme.palette.lightGrey.main
            : undefined,
        },
      }}>
      <Box
        py={2.5}
        flex={1}
        component={isRoute ? Link : 'div'}
        to={route || ''}
        onClick={onClick}
        display="flex"
        alignItems="center"
        gap={2}
        sx={{
          textDecoration: 'none',
          cursor: isClickable ? 'pointer' : 'default',
        }}>
        <Icon
          sx={{
            width: '28px',
            height: '28px',
            color:
              theme.palette[isComplete ? 'secondary' : 'lightOutlineGrey'].main,
          }}
        />
        <Stack>
          <Typography
            variant="h3"
            component="p"
            fontSize={theme.typography.pxToRem(16)}
            color={
              isComplete
                ? theme.palette.outlineGrey.main
                : theme.palette.darkGrey.main
            }>
            {step.name}
          </Typography>
          {!isComplete && 'description' in step && (
            <Typography
              variant="body2"
              component="p"
              color={theme.palette.medGrey.main}>
              {step.description}
            </Typography>
          )}
        </Stack>
        {category === 'SETUP' && (
          <Typography
            variant="body2"
            component="p"
            sx={{
              ml: 'auto',
            }}
            color={theme.palette.medGrey.main}>
            {categoryProgress} / {categoryTotal}{' '}
            <Typography
              display={{
                xs: 'none',
                sm: 'inline',
              }}
              component="span"
              variant="inherit">
              Completed
            </Typography>
          </Typography>
        )}
      </Box>
      {step.isSkippable && onboarding?.status === 'INCOMPLETE' && (
        <LoadingButton
          loading={isStepBeingSkipped}
          disabled={isSkipping}
          disableRipple
          onClick={onSkip}
          variant="text"
          sx={{
            flexShrink: 0,
            p: 1,
            minWidth: 0,
            ml: 'auto',
          }}>
          <CloseRounded
            sx={{
              width: '14px',
              height: '14px',
              opacity: isStepBeingSkipped ? 0 : 1,
            }}
            htmlColor={theme.palette.lightOutlineGrey.main}
          />
        </LoadingButton>
      )}
    </Box>
  );
}

export default StepRow;
